import React, { useEffect, useState, useMemo } from 'react';
import { Accordion, Button, Card, useAccordionButton } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import pluralize from 'pluralize';
import { useEffectOnce } from 'usehooks-ts';
import DataTable from 'react-data-table-component';
import { TableColumn } from 'react-data-table-component';

import { DownloadLinkButton } from '@components/Base';
import { Document } from '@models/Document';
import { formatDateTime } from '@utils/datetime';

import {
  documentsFetch,
  DocumentsFetchPayload,
  documentCreate,
  DocumentCreatePayload,
  stateClear,
} from './action';
import { selectFeatureDocument } from './selector';
import { DocumentFormModal, DocumentFormValues } from './document-form-modal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';

const customStyles = {
  headCells: {
    style: {
      fontWeight: 'bold',
    },
  },
};

const Toggle = ({ children, eventKey }: { children: React.ReactNode; eventKey: string }) => {
  const handleClick = useAccordionButton(eventKey);

  return (
    <Card.Link className="c-pointer link-info" onClick={handleClick}>
      {children}
    </Card.Link>
  );
};

type Props = Pick<DocumentsFetchPayload, 'documentableType' | 'documentableId' | 'documentType'> & {
  canCreate?: boolean;
};

const DocumentCardBody = ({ documentableType, documentableId, documentType, canCreate }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const state = useSelector(selectFeatureDocument);
  const { documents, isLoading, pagination } = state[documentType];
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [show, setShow] = useState(false);

  const columns = useMemo(() => [
    {
      name: t('common:createdDate'),
      selector: ({ createdAt }) => formatDateTime(createdAt),
      minWidth: '180px',
    },
    {
      name: t('common:creator'),
      selector: ({ creator }) => creator.name,
    },
    {
      name: t('common:name'),
      selector: ({ name }) => name,
    },
    {
      name: t('common:document'),
      selector: ({ document }) => <DownloadLinkButton href={document} />,
    },
  ] as TableColumn<Document>[], [documents]);

  useEffect(() => {
    const payload: DocumentsFetchPayload = { documentableType, documentableId, documentType, page, perPage };
    dispatch(documentsFetch(payload));
  }, [documentableType, documentableId, documentType, page, perPage]);

  useEffectOnce(() => {
    return () => {
      dispatch(stateClear());
    };
  });

  return (
    <>
      <Card.Body>
        <Accordion flush>
          <Card.Title className="d-flex flex-row justify-content-between align-items-center">
            <Toggle eventKey="0">
              {documentType} {pluralize(t('common:document'), 2)} ({pagination?.total ?? 0})
            </Toggle>
            {canCreate && (
              <Button onClick={() => setShow(true)} variant="outline-primary">
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                {t('common:add')}
              </Button>
            )}
          </Card.Title>
          <Accordion.Collapse eventKey="0">
            <DataTable
              columns={columns}
              data={documents ?? []}
              customStyles={customStyles}
              fixedHeader
              pagination
              paginationServer
              progressPending={isLoading}
              paginationPerPage={perPage}
              paginationTotalRows={pagination?.total ?? 0}
              onChangeRowsPerPage={(newPerPage) => setPerPage(newPerPage)}
              onChangePage={(newPage) => setPage(newPage)}
            />
          </Accordion.Collapse>
        </Accordion>
      </Card.Body>

      <DocumentFormModal
        show={show}
        title={documentType}
        onHide={() => setShow(false)}
        onSubmit={(values: DocumentFormValues) => {
          const payload: DocumentCreatePayload = { documentableType, documentableId, documentType, ...values };
          dispatch(documentCreate(payload));
          setShow(false);
        }}
      />
    </>
  );
};

export { DocumentCardBody };
